import { comics, comicCategories, comicRegions, comicTags } from "@/mocks/comics";
import type { Chapter, Comic, ComicCategory, FilterState } from "@/types/comic";

export const allComics: Comic[] = comics as Comic[];
export const allCategories: ComicCategory[] = comicCategories as ComicCategory[];
export const regions = comicRegions;
export const tagOptions = comicTags;

export const categoryFilters = [
  { id: "all", name: "全部" },
  ...allCategories.map((category) => ({ id: category.id, name: category.name })),
];

export function getComicById(id: string | undefined): Comic | undefined {
  return allComics.find((comic) => comic.id === id);
}

export function getCategoryById(id: string | undefined): ComicCategory | undefined {
  return allCategories.find((category) => category.id === id);
}

export function formatCount(count: number): string {
  if (count >= 10000) {
    return `${(count / 10000).toFixed(1)}万`;
  }
  return `${count}`;
}

function timeValue(updatedAt: string): number {
  return new Date(updatedAt.replace(/-/g, "/")).getTime();
}

export function latestComics(limit?: number): Comic[] {
  const list = [...allComics].sort((a, b) => timeValue(b.updatedAt) - timeValue(a.updatedAt));
  return typeof limit === "number" ? list.slice(0, limit) : list;
}

export function popularComics(limit?: number): Comic[] {
  const list = [...allComics].sort((a, b) => b.viewCount - a.viewCount);
  return typeof limit === "number" ? list.slice(0, limit) : list;
}

export function relatedComics(comic: Comic, limit = 4): Comic[] {
  const sameCategory = allComics.filter(
    (item) => item.id !== comic.id && item.categoryId === comic.categoryId
  );
  const sameTag = allComics.filter(
    (item) =>
      item.id !== comic.id &&
      item.categoryId !== comic.categoryId &&
      item.tags.some((tag) => comic.tags.includes(tag))
  );
  const others = allComics.filter(
    (item) => item.id !== comic.id && !sameCategory.includes(item) && !sameTag.includes(item)
  );
  return [...sameCategory, ...sameTag, ...others].slice(0, limit);
}

export function searchComics(keyword: string): Comic[] {
  const word = keyword.trim().toLowerCase();
  if (!word) {
    return [];
  }
  return allComics.filter((comic) => {
    const haystack = [
      comic.title,
      comic.author,
      comic.categoryName,
      comic.intro,
      ...comic.tags,
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(word);
  });
}

export function filterComics(
  list: Comic[],
  filters: FilterState,
  categoryId = "all"
): Comic[] {
  let result = list.filter((comic) => {
    if (categoryId !== "all" && comic.categoryId !== categoryId) {
      return false;
    }
    if (filters.tag !== "全部" && !comic.tags.includes(filters.tag)) {
      return false;
    }
    if (filters.region !== "全部" && comic.region !== filters.region) {
      return false;
    }
    if (filters.status !== "全部" && comic.status !== filters.status) {
      return false;
    }
    return true;
  });

  if (filters.sort === "热度优先") {
    result = [...result].sort((a, b) => b.viewCount - a.viewCount);
  } else if (filters.sort === "收藏优先") {
    result = [...result].sort((a, b) => b.likeCount - a.likeCount);
  } else {
    result = [...result].sort((a, b) => timeValue(b.updatedAt) - timeValue(a.updatedAt));
  }

  return result;
}

const chapterSubtitles = [
  "风起",
  "旧约",
  "未寄出的信",
  "夜色将至",
  "另一种答案",
  "在人群里",
  "第二次相遇",
  "灯下长谈",
  "雨停之后",
  "告别练习",
  "逆风",
  "缝隙",
  "归途",
  "沉默的证人",
  "远处的钟声",
];

function shiftDays(base: string, days: number): string {
  const date = new Date(base.replace(/-/g, "/"));
  date.setDate(date.getDate() - days);
  const pad = (value: number) => `${value}`.padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function buildChapters(comic: Comic): Chapter[] {
  const chapters: Chapter[] = [];
  for (let index = 0; index < comic.chapterCount; index += 1) {
    const number = comic.chapterCount - index;
    const subtitle = chapterSubtitles[(number + comic.title.length) % chapterSubtitles.length];
    chapters.push({
      id: `${comic.id}-${number}`,
      title: index === 0 ? comic.latestChapter : `第${number}话 ${subtitle}`,
      updatedAt: shiftDays(comic.updatedAt, index * 3),
    });
  }
  return chapters;
}

export const defaultFilters: FilterState = {
  tag: "全部",
  region: "全部",
  status: "全部",
  sort: "更新时间",
};

export const statusOptions = ["全部", "连载", "完结"];
export const sortOptions = ["更新时间", "热度优先", "收藏优先"];